import { useState, useContext, KeyboardEvent, useEffect } from 'react';

import { useNavigate } from 'react-router-dom';

import { useQuery } from '@tanstack/react-query';

import {
  Flex,
  Text,
  useColorModeValue,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Table,
  Thead,
  Tr,
  Th,
  Tbody,
  Td,
  Box,
  Button,
  Spinner,
  InputGroup,
  Input,
  Stack,
  IconButton,
  Tooltip,
  Collapse,
} from '@chakra-ui/react';

import {
  AddIcon,
  SearchIcon,
  SmallCloseIcon,
  RepeatIcon,
} from '@chakra-ui/icons';

import Paginate from 'components/paginate/Paginate';
import Card from 'components/card/Card';
import SelectSearch from 'components/fields/SelectSearch';

import { apiGetLeadTags, apiGetBranches, apiGetUsers } from 'api';

import type {
  APILeadTagData,
  APIBranchData,
  APIUserListResultData,
} from 'types/api.types';

import { AuthContext } from 'contexts/AuthContext';

import { useUrlFilters } from 'hooks/useUrlFilters';

import { ROLE_ADMIN, ROLE_MANAGER } from 'variables/constants';

const TABLE_HEADERS = [
  'Name',
  'Created By',
  'Branch',
  'Created At',
  'Updated At',
];

const TAG_FILTER_KEYS = ['name', 'branch', 'user'] as const;

const TagListView = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const {
    setFilters,
    page,
    setPage,
    resetAll,
    queryString,
    searchParams,
  } = useUrlFilters(TAG_FILTER_KEYS);

  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');
  const inputBg = useColorModeValue('white', 'navy.900');
  const hoverBgColor = useColorModeValue('gray.200', 'whiteAlpha.100');

  const isAdmin = user.role === ROLE_ADMIN;
  const isManager = user.role === ROLE_MANAGER;
  const canAccessAdvancedFilters = isAdmin || isManager;

  const [showSearchBar, setShowSearchBar] = useState(false);
  const [nameSearch, setNameSearch] = useState('');
  const [selectedBranch, setSelectedBranch] = useState('');
  const [selectedUser, setSelectedUser] = useState('');

  useEffect(() => {
    setNameSearch(searchParams.get('name') ?? '');
    setSelectedBranch(searchParams.get('branch') ?? '');
    setSelectedUser(searchParams.get('user') ?? '');
  }, [searchParams]);

  const { error, data, isLoading, isFetching, refetch } = useQuery({
    queryKey: ['tag-list', page, queryString],
    queryFn: () => apiGetLeadTags(page + 1, queryString),
  }) as {
    error: any;
    data: { results: APILeadTagData[]; count: number };
    isLoading: boolean;
    isFetching: boolean;
    refetch: () => void;
  };

  // Query to fetch branches
  const { data: branchesData, isLoading: isBranchesLoading } = useQuery({
    queryKey: ['branches-all'],
    queryFn: () => apiGetBranches(null, 'all'),
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled: canAccessAdvancedFilters, // Only fetch if user is admin or manager
  }) as {
    data: APIBranchData[] | undefined;
    isLoading: boolean;
    isFetching: boolean;
    refetch: () => void;
  };

  // Query to fetch users
  const { data: usersData, isLoading: isUsersLoading } = useQuery({
    queryKey: ['all-users'],
    queryFn: () => apiGetUsers(null, 'all'),
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled: canAccessAdvancedFilters, // Only fetch if user is admin or manager
  }) as {
    data: APIUserListResultData[] | undefined;
    isLoading: boolean;
    isFetching: boolean;
    refetch: () => void;
  };

  const handleCreateTag = () => {
    navigate('/admin/settings/tags/new');
  };

  const handleEditTag = (tag: APILeadTagData) => {
    navigate(`/admin/settings/tags/edit/${tag.id}`);
  };

  const canEditTag = (tag: APILeadTagData) => {
    return (
      [ROLE_ADMIN, ROLE_MANAGER].includes(user.role) || tag.user?.id === user.id
    );
  };

  const handleSearch = () => {
    setFilters({
      name: nameSearch.trim(),
      ...(canAccessAdvancedFilters && { branch: selectedBranch.trim() }),
      ...(canAccessAdvancedFilters && { user: selectedUser.trim() }),
    });
  };

  const resetSearch = () => {
    setNameSearch('');
    if (canAccessAdvancedFilters) {
      setSelectedBranch('');
      setSelectedUser('');
    }
    resetAll();
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  if (error) {
    return (
      <Card>
        <Box p="20px" pb="0">
          <Alert status="error">
            <AlertIcon />
            <AlertTitle>Error</AlertTitle>
            <AlertDescription>{JSON.stringify(error)}</AlertDescription>
          </Alert>
        </Box>
      </Card>
    );
  }

  return (
    <Card
      flexDirection="column"
      w="100%"
      px="0px"
      overflowX={{ sm: 'scroll', lg: 'hidden' }}
    >
      <Flex px="20px" justifyContent="space-between" align="center" mb="20px">
        <Text
          color={textColor}
          fontSize="22px"
          fontWeight="700"
          lineHeight="100%"
        >
          Lead Tags
        </Text>
        <Flex gap={2}>
          <Tooltip
            label={showSearchBar ? 'Hide search' : 'Show search'}
            hasArrow
          >
            <IconButton
              aria-label={showSearchBar ? 'Hide search' : 'Show search'}
              icon={<SearchIcon />}
              variant={showSearchBar ? 'solid' : 'ghost'}
              colorScheme={showSearchBar ? 'yellow' : 'gray'}
              onClick={() => setShowSearchBar(!showSearchBar)}
              size="md"
            />
          </Tooltip>
          <Tooltip label="Refresh tags" hasArrow>
            <IconButton
              aria-label="Refresh tags"
              icon={<RepeatIcon />}
              onClick={refetch}
              variant="ghost"
              isLoading={isLoading || isFetching}
            />
          </Tooltip>
          <Button
            leftIcon={<AddIcon />}
            variant="brand"
            size="md"
            onClick={handleCreateTag}
          >
            New Tag
          </Button>
        </Flex>
      </Flex>

      <Collapse in={showSearchBar} animateOpacity>
        <Box
          px="20px"
          pb="20px"
          borderBottom="1px solid"
          borderColor={borderColor}
        >
          <Stack
            spacing={4}
            direction={{ base: 'column', lg: 'row' }}
            align="flex-end"
          >
            <Box flex="1">
              <InputGroup>
                <Input
                  variant="main"
                  placeholder="Search by tag name"
                  value={nameSearch}
                  onChange={(e) => setNameSearch(e.target.value)}
                  bg={inputBg}
                  size="md"
                  onKeyDown={handleKeyDown}
                />
              </InputGroup>
            </Box>

            {canAccessAdvancedFilters && (
              <Box flex="1">
                <SelectSearch
                  options={
                    branchesData?.map((branch) => ({
                      value: branch.id.toString(),
                      label: branch.name,
                    })) || []
                  }
                  value={selectedBranch}
                  onChange={(value: any) =>
                    setSelectedBranch(value?.toString() || '')
                  }
                  placeholder="All branches"
                  isDisabled={isBranchesLoading}
                  allowClear
                />
              </Box>
            )}

            {canAccessAdvancedFilters && (
              <Box flex="1">
                <SelectSearch
                  options={
                    usersData?.map((user) => ({
                      value: user.user.id.toString(),
                      label: `${user.user.first_name} ${user.user.last_name}`,
                    })) || []
                  }
                  value={selectedUser}
                  onChange={(value: any) =>
                    setSelectedUser(value?.toString() || '')
                  }
                  placeholder="All users"
                  isDisabled={isUsersLoading}
                  allowClear
                />
              </Box>
            )}

            <Flex gap={2}>
              <Button
                onClick={handleSearch}
                leftIcon={<SearchIcon />}
                size="md"
                variant="brand"
              >
                Search
              </Button>
              <Button
                onClick={resetSearch}
                variant="outline"
                leftIcon={<SmallCloseIcon />}
                size="md"
              >
                Reset
              </Button>
            </Flex>
          </Stack>
        </Box>
      </Collapse>

      <Table variant="simple" color="gray.500">
        <Thead>
          <Tr>
            {TABLE_HEADERS.map((header) => (
              <Th
                key={header}
                pe="10px"
                borderColor={borderColor}
                color="gray.400"
              >
                {header}
              </Th>
            ))}
          </Tr>
        </Thead>
        <Tbody>
          {isLoading && (
            <Tr>
              <Td colSpan={5} textAlign="center">
                <Flex align="center" justify="center" mt="20px">
                  <Spinner size="sm" mr="10px" /> Loading tags...
                </Flex>
              </Td>
            </Tr>
          )}

          {data?.results.map((tag) => (
            <Tr
              key={tag.id}
              cursor={canEditTag(tag) ? 'pointer' : 'default'}
              _hover={{
                bg: canEditTag(tag) ? hoverBgColor : 'inherit',
              }}
              onClick={() => {
                if (canEditTag(tag)) {
                  handleEditTag(tag);
                }
              }}
            >
              <Td>
                <Flex align="center">
                  <Text color={textColor} fontSize="sm" fontWeight="700">
                    {tag.name}
                  </Text>
                </Flex>
              </Td>
              <Td>
                <Flex align="center">
                  <Text color={textColor} fontSize="sm">
                    {tag.user
                      ? `${tag.user.first_name} ${tag.user.last_name}`
                      : 'N/A'}
                  </Text>
                </Flex>
              </Td>
              <Td>
                <Flex align="center">
                  <Text color={textColor} fontSize="sm">
                    {tag.branch ? tag.branch.name : 'N/A'}
                  </Text>
                </Flex>
              </Td>
              <Td>
                <Flex align="center">
                  <Text color={textColor} fontSize="sm">
                    {formatDate(tag.created_at)}
                  </Text>
                </Flex>
              </Td>
              <Td>
                <Flex align="center">
                  <Text color={textColor} fontSize="sm">
                    {formatDate(tag.updated_at)}
                  </Text>
                </Flex>
              </Td>
            </Tr>
          ))}
        </Tbody>
      </Table>

      {data && data.results.length === 0 && (
        <Box textAlign="center" w="100%" mt="20px">
          No tags found
        </Box>
      )}

      <Paginate page={page} count={data?.count || 0} onSetPage={setPage} />
    </Card>
  );
};

export default TagListView;
